import { useState } from "react";
import classNames from "classnames";
import { DoughnutChartCard } from "components/molecules/DoughnutChartCard";
import { HorizontalProgressBarCard } from "components/molecules/HorizontaProgressBarCard";
import { AIRecommendationCard } from "components/molecules/AIRecommendationCard";
import { Carousel, FetchError } from "components/molecules";
import {
  useGetOverallProgressQuery,
  useGetTestScoresAndPerformanceQuery,
} from "services/homeApi";
import { Spinner } from "components/atoms/Icon";
import { decodeToken } from "utils/functions/decodeToken";
import { GRE_TEST_TYPE } from "utils/constants";
import { mixpanelHelper } from "utils/functions/mixPanelHelper";
import { useGetAiRecommendationsQuery } from "services/aiRecommendations";
import "./index.scss";

const TEST_SCORES_WIDGET_MAX_BARS = 3;
const TEST_SCORES_WIDGET_LABEL_MAX_LENGTH = 20;

interface IProps {
  theme: "light" | "dark";
  onClick: (type: string) => void;
}

export const ProgressCardSection = ({ theme, onClick }: IProps) => {
  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const [isShowOverallProgressSummary] = useState(
    payload &&
    payload.hasOwnProperty("showoverallprogresssummary") &&
    payload.showoverallprogresssummary.toLowerCase() === "true"
  );

  const [isShowPerformanceSummary] = useState(
    payload &&
    payload.hasOwnProperty("showperformancesummary") &&
    payload.showperformancesummary.toLowerCase() === "true"
  );

  const [isShowStudyRecommendations] = useState(
    payload &&
    payload.hasOwnProperty("showstudyrecommendations") &&
    payload.showstudyrecommendations.toLowerCase() === "true"
  );

  const { data, isLoading, isFetching, isError } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const {
    data: testScoreData,
    isLoading: isTestScoreLoading,
    isFetching: isTestScoreFetching,
    isError: isTestScoreError,
  } = useGetTestScoresAndPerformanceQuery();

  const {
    data: aiRecommendData,
    isLoading: isAiRecommendLoading,
    isError: isAiRecommendError,
  } = useGetAiRecommendationsQuery();

  const getColor = (color: string | null) => {
    switch (color) {
      case "RED":
        return "red";
      case "YELLOW":
        return "yellow";
      case "GREEN":
        return "green";
      case null:
        return "grey";
      default:
        return "grey";
    }
  };

  const tests = Array.isArray(testScoreData)
    ? testScoreData
    : (testScoreData?.Tests ?? []);
  const hasTestData = Array.isArray(tests) && tests.length > 0;
  const isGre = payload?.testtype === GRE_TEST_TYPE;

  const widgetDataSet = hasTestData
    ? !isGre
      ? tests.slice(0, TEST_SCORES_WIDGET_MAX_BARS).map((t: any) => ({
          complete: t.TestScore ?? 0,
          total: t.TestScoreMax ?? 0,
          label:
            (t.TestName?.length ?? 0) > TEST_SCORES_WIDGET_LABEL_MAX_LENGTH
              ? `${(t.TestName ?? "").substring(0, TEST_SCORES_WIDGET_LABEL_MAX_LENGTH)}...`
              : (t.TestName ?? ""),
          progressColor: getColor(t.Color ?? null),
        }))
      : (() => {
          const flat: { complete: number; total: number; label: string; labelSecondary?: string; progressColor: string }[] = [];
          for (const t of tests) {
            if (!t.TestScores?.length) continue;
            for (const s of t.TestScores) {
              const scoreName = s.SubjectName ?? s.Name ?? "";
              const truncatedSecondary =
                scoreName.length > TEST_SCORES_WIDGET_LABEL_MAX_LENGTH
                  ? `${scoreName.substring(0, TEST_SCORES_WIDGET_LABEL_MAX_LENGTH)}...`
                  : scoreName;
              flat.push({
                complete: s.Score ?? 0,
                total: s.ScoreMax ?? t.TestScoreMax ?? 0,
                label:
                  (t.TestName?.length ?? 0) > TEST_SCORES_WIDGET_LABEL_MAX_LENGTH
                    ? `${(t.TestName ?? "").substring(0, TEST_SCORES_WIDGET_LABEL_MAX_LENGTH)}...`
                    : (t.TestName ?? ""),
                labelSecondary: truncatedSecondary,
                progressColor: getColor(s.Color ?? t.Color ?? null),
              });
            }
          }
          return flat.slice(0, TEST_SCORES_WIDGET_MAX_BARS);
        })()
    : [];

  const viewScoreAnalytics = () => {
    mixpanelHelper.track("View Score", {
      courseProgress: "",
      testType: "",
      productCode: productCode,
      psoCode: offeringCode,
      userID: userId,
    });
  };

  const totalCounts = data
    ? {
      complete:
        data.DiagnosticsCompletedCount +
        data.AttendanceCompletedCount +
        data.HomeworkCompletedCount +
        data.TestsCompletedCount +
        data.TopicFocusCompletedCount,
      inComplete:
        data.DiagnosticsIncompleteCount +
        data.AttendanceIncompleteCount +
        data.HomeworkIncompleteCount +
        data.TestsIncompleteCount +
        data.TopicFocusIncompleteCount,
      upComing:
        data.DiagnosticsUpcomingCount +
        data.AttendanceUpcomingCount +
        data.HomeworkUpcomingCount +
        data.TestsUpcomingCount +
        data.TopicFocusUpcomingCount,
    }
    : null;

  return (
    <Carousel>
      {isShowOverallProgressSummary && (
        <div
          className={classNames("doughnut-card-section")}
          id="doughnut-card-section"
        >
          {isLoading || isFetching ? (
            <Spinner theme={theme} />
          ) : isError ? (
            <div className={classNames("error-text-container")}>
              <FetchError content="Oops! failed to load resources." />
            </div>
          ) : (
            data &&
            totalCounts && (
              <DoughnutChartCard
                complete={totalCounts.complete}
                inComplete={totalCounts.inComplete}
                upComing={totalCounts.upComing}
                theme={theme}
                handleClick={() => onClick("doughnut")}
              />
            )
          )}
        </div>
      )}

      {isShowPerformanceSummary &&
        <div
          className={classNames("horizontal-progress-bar-card-section")}
          id="test-scores-and-performance"
        >
          {isTestScoreLoading || isTestScoreFetching ? (
            <Spinner theme={theme} />
          ) : isTestScoreError ? (
            <div className={classNames("daughnut-chart-error-container")}>
              <FetchError content="Oops! failed to load resources." />
            </div>
          ) : (
            testScoreData &&
            hasTestData && (
              <HorizontalProgressBarCard
                dataSet={widgetDataSet}
                handleClick={() => {
                  viewScoreAnalytics();
                  onClick("horizontal");
                }}
              />
            )
          )}
        </div>}

      {isShowStudyRecommendations && (
        <div className={classNames("ai-recommendation-card-section")}>
          {isAiRecommendLoading ? (
            <Spinner theme={theme} />
          ) : isAiRecommendError ? (
            <div className={classNames("daughnut-chart-error-container")}>
              <FetchError content="Oops! failed to load resources." />
            </div>
          ) : (
            aiRecommendData && (
              <AIRecommendationCard
                theme={theme}
                data={aiRecommendData}
                handleClick={() => onClick("ai")}
              />
            )
          )}
        </div>
      )}
    </Carousel>
  );
};
